import apiClient from '../utils/apiClient';
import type { PRStats } from '../pages/PRStats/types';
import { GITHUB_TO_SLACK } from '../constants/github';
import { safeJSONParse } from '@/utils/helper';

interface PRDetails {
	url: string;
	title: string;
	createdAt: string;
}

interface PRStatsData {
	totalPRs: number;
	avgDaysToClose: number;
	openPRs?: number;
	totalDaysOpen?: number;
	commentsOnOthersPRs: number;
	prReviews: number;
	engineeringScore: number;
	seniorEngineeringScore: number;
	prsClosedInRange?: number;
	linesAdded?: 0;
	linesRemoved?: 0;
	significantLines?: 0;
	details?: {
		prs: PRDetails[];
		reviews: PRDetails[];
		comments: PRDetails[];
	};
}

interface PRStatsResponse {
	stats: Record<string, PRStatsData>;
	rateLimit: {
		limit: string;
		remaining: string;
		reset: string;
		resetDate: string;
		used: string;
	};
	isDummyData?: boolean;
	requestCount?: number;
}

interface CachedPRStats {
	data: PRStats[];
	timestamp: number;
	params: { startDate?: string; endDate?: string };
}

const CACHE_KEY = 'pr_stats_cache';
const CACHE_DURATION = 25 * 60 * 1000; // 5 minutes

function getCachedStats(): CachedPRStats | null {
	const cached = localStorage.getItem(CACHE_KEY);
	if (!cached) {
		return null;
	}

	return safeJSONParse(cached, null);
}

function setCachedStats(data: PRStats[], params: { startDate?: string; endDate?: string }) {
	const cache: CachedPRStats = {
		data,
		timestamp: Date.now(),
		params,
	};
	localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
}

export async function fetchPRStats(
	params: { startDate?: string; endDate?: string } = {},
	forceRefresh = false,
	password: string,
): Promise<{
	data: PRStats[];
	fromCache: boolean;
	rateLimit?: PRStatsResponse['rateLimit'];
	isDummyData?: boolean;
}> {
	if (!forceRefresh) {
		const cached = getCachedStats();
		if (
			cached &&
			Date.now() - cached.timestamp < CACHE_DURATION &&
			cached.params.startDate === params.startDate &&
			cached.params.endDate === params.endDate
		) {
			return { data: cached.data, fromCache: true };
		}
	}

	const response = await apiClient.request({
		method: 'GET',
		endPoint: '/api/github/pr-stats',
		params: {
			...params,
			password: password || localStorage.getItem('pr_stats_password') || '',
		},
		headers: {
			'Content-Type': 'application/json',
		},
	});

	if (!response) {
		throw new Error('Invalid response format from PR stats endpoint');
	}

	const transformedData = Object.entries(response as Record<string, PRStatsData>).map(
		([author, stats]) => ({
			author,
			slackName: GITHUB_TO_SLACK[author] || 'Unknown',
			totalPRs: stats.totalPRs || 0,
			avgDaysToClose: stats.avgDaysToClose || 0,
			openPRs: stats.openPRs || 0,
			totalDaysOpen: stats.totalDaysOpen || 0,
			commentsOnOthersPRs: stats.commentsOnOthersPRs || 0,
			prReviews: stats.prReviews || 0,
			engineeringScore: stats.engineeringScore || 0,
			seniorEngineeringScore: stats.seniorEngineeringScore || 0,
			details: stats.details || { prs: [], reviews: [], comments: [] },
			prsClosedInRange: stats.prsClosedInRange || 0,
			linesAdded: stats.linesAdded || 0,
			linesRemoved: stats.linesRemoved || 0,
			significantLines: stats.significantLines || 0,
		}),
	);

	setCachedStats(transformedData, params);

	return {
		data: transformedData,
		fromCache: false,
		rateLimit: response.rateLimit,
		isDummyData: response.isDummyData,
	};
}
